export function timeMinutes(value) {
  const match = String(value || '').match(/(?:^|[ T])(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const [, hour, minute, second = '0'] = match;
  if (+hour > 24 || +minute > 59 || +second > 59 || (+hour === 24 && (+minute || +second))) return null;
  return +hour * 60 + +minute + +second / 60;
}

export function formatMinutes(value) {
  const days = Math.floor(value / 1440);
  const minutes = ((value % 1440) + 1440) % 1440;
  const time = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(Math.floor(minutes % 60)).padStart(2, '0')}`;
  return days ? `${days > 0 ? '+' : ''}${days}d ${time}` : time;
}

function calendarDay(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T]|$)/);
  if (!match) return null;
  const date = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3]));
  if (date.getUTCFullYear() !== +match[1] || date.getUTCMonth() !== +match[2] - 1 || date.getUTCDate() !== +match[3]) return null;
  return date.getTime() / 86400000;
}

export function reviewTicks(rows, type) {
  let start = type === 'overtime' ? 0 : 9 * 60;
  let end = type === 'overtime' ? 1440 : 18 * 60;
  rows.forEach(person => person.records.forEach(record => {
    start = Math.min(start, Math.floor(record.start / 30) * 30);
    end = Math.max(end, Math.ceil(record.end / 30) * 30);
  }));
  return Array.from({ length: (end - start) / 30 }, (_, index) => start + index * 30);
}

// Review uses recorded time ranges; department metadata does not split shifts.
export function buildReviewRows(sources, workDate, departmentLabels = []) {
  const people = new Map();
  let invalidCount = 0;
  sources.forEach(({ department: sourceDepartment, staffList }, sourceIndex) => {
    staffList.forEach((staff, index) => {
      const department = staff.department != null
        ? staff.department : (sourceDepartment || staff.deptName || staff.staffDepartment || '');
      let hours = staff.staffListHours;
      try { hours = typeof hours === 'string' ? JSON.parse(hours) : hours; } catch (error) { hours = null; }
      const startValue = staff.startTime || (hours && hours[0]);
      const endValue = staff.endTime || (hours && hours[1]);
      let start = timeMinutes(startValue);
      let end = timeMinutes(endValue);
      const startDay = calendarDay(startValue);
      const endDay = calendarDay(endValue);
      const selectedDay = calendarDay(workDate);
      const baseDay = selectedDay === null ? startDay : selectedDay;
      // Respect explicit dates for overnight work. Do not guess that a reversed
      // same-day or time-only range is an overnight shift.
      if (baseDay !== null && startDay !== null && endDay !== null) {
        if (start !== null) start += (startDay - baseDay) * 1440;
        if (end !== null) end += (endDay - baseDay) * 1440;
      }
      // Prefer stable employee identifiers over the timesheet row's id.
      const identity = [staff.staffId, staff.staffCode, staff.staffNo, staff.code, staff.id].find(value => value !== null && value !== undefined && value !== '');
      const name = staff.name || staff.staffName || String(identity || '—');
      const key = identity === undefined ? `name:${name.trim().toLowerCase()}` : `id:${identity}`;
      if (start === null || end === null || end <= start) { invalidCount++; return; }
      if (!people.has(key)) people.set(key, { key, name, records: [], conflicts: [], gaps: [] });
      people.get(key).records.push({
        key: `${sourceIndex}:${index}`, department: String(department), start, end,
        departmentIds: staff.departmentIds && staff.departmentIds.length ? staff.departmentIds.map(String) : [String(department)],
        overlap: false,
      });
    });
  });
  const rows = Array.from(people.values());
  rows.forEach(person => {
    person.records.sort((a, b) => a.start - b.start || a.end - b.end);
    // Compare against the union of covered time, not just the previous record:
    // a nested/overlapping shift must not introduce a false gap.
    let coveredEnd = person.records[0].end;
    person.records.slice(1).forEach(record => {
      if (record.start > coveredEnd) person.gaps.push({ start: coveredEnd, end: record.start });
      coveredEnd = Math.max(coveredEnd, record.end);
    });
    const laneEnds = [];
    person.records.forEach((record, index) => {
      let lane = laneEnds.findIndex(end => end <= record.start);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = record.end;
      record.lane = lane;
      for (let next = index + 1; next < person.records.length; next++) {
        const other = person.records[next];
        if (other.start >= record.end) break;
        record.overlap = other.overlap = true;
        person.conflicts.push({ start: Math.max(record.start, other.start), end: Math.min(record.end, other.end), records: [record, other] });
      }
    });
    person.lanes = laneEnds.length;
  });
  const departmentLabel = person => {
    // Cross-department employees stay on one row, ordered by their first shift.
    const department = person.records[0].department;
    const option = departmentLabels.find(item => String(item.value) === department || item.label === department);
    return String(option ? option.label : department).trim();
  };
  rows.sort((a, b) => {
    const aDepartment = departmentLabel(a);
    const bDepartment = departmentLabel(b);
    if (!aDepartment) return bDepartment ? 1 : 0;
    if (!bDepartment) return -1;
    return aDepartment.localeCompare(bDepartment, undefined, { numeric: true });
  });
  return { rows, invalidCount };
}

export function filterReviewRows(rows, department) {
  if (!department) return rows;
  // Keep all records for matching people so cross-department conflicts stay visible.
  return rows.filter(person => person.records.some(record => record.departmentIds.includes(String(department))));
}
